import { useQuery } from '@tanstack/react-query';
import { CENTRO_DE_CUSTO, type TipoLancamento } from '@arena/shared/dominio';
import { ROTULO_CENTRO_DE_CUSTO, type FiltroDeOrigem } from '@arena/shared/schemas';
import { financeiroApi } from '../api/financeiro.js';
import { Botao, Campo, Escolhas } from '../ui/primitivos.jsx';
import {
  definirFiltro,
  limparFiltrosDoExtrato,
  temFiltroNoExtrato,
  type FiltrosDoExtrato,
} from './formatos.js';

/**
 * Os filtros do extrato: tipo, centro de custo, categoria e origem
 * (manual ou automático).
 *
 * Moram na URL — quem escreve é `mudar`, o mesmo helper de `#v-planos` —,
 * e por isso o link colado abre o mesmo recorte e o F5 não o perde.
 *
 * Os filtros só ESTREITAM: o servidor os soma ao escopo do perfil. A
 * Gestão não recebe a conta da folha na lista de categorias, e se alguém
 * colar um link com o id dela, o servidor devolve a lista vazia — o campo
 * aqui avisa "Categoria indisponível" em vez de fingir que está em "Todas".
 */

type TipoOuTodos = TipoLancamento | 'todos';
type OrigemOuTodas = FiltroDeOrigem | 'todas';

const OPCOES_DE_TIPO: readonly { valor: TipoOuTodos; rotulo: string }[] = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'Entrada', rotulo: 'Entradas' },
  { valor: 'Saida', rotulo: 'Saídas' },
];

const OPCOES_DE_ORIGEM: readonly { valor: OrigemOuTodas; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todos' },
  { valor: 'manual', rotulo: 'Manuais' },
  { valor: 'automatico', rotulo: 'Automáticos' },
];

export function BarraDeFiltros({
  filtros,
  mudar,
}: {
  filtros: FiltrosDoExtrato;
  mudar: (mudanca: (p: URLSearchParams) => void) => void;
}) {
  // A mesma chave do modal de lançamento: as duas telas compartilham a lista.
  const categoriasQuery = useQuery({
    queryKey: ['financeiro', 'categorias'],
    queryFn: financeiroApi.categorias,
  });
  const categorias = categoriasQuery.data ?? [];
  const entradas = categorias.filter((c) => c.tipo === 'Entrada');
  const saidas = categorias.filter((c) => c.tipo === 'Saida');
  const categoriaConhecida = categorias.some((c) => c.id === filtros.categoriaId);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: '12px 16px',
        marginBottom: 14,
      }}
    >
      <Campo rotulo="Tipo">
        <Escolhas
          aria-label="Filtrar pelo tipo"
          opcoes={OPCOES_DE_TIPO}
          valor={filtros.tipo ?? 'todos'}
          onChange={(valor) =>
            mudar((p) => definirFiltro(p, 'tipo', valor === 'todos' ? undefined : valor))
          }
        />
      </Campo>
      <Campo rotulo="Centro de custo">
        <select
          aria-label="Filtrar pelo centro de custo"
          value={filtros.centroDeCusto ?? ''}
          onChange={(e) => mudar((p) => definirFiltro(p, 'centro', e.target.value))}
        >
          <option value="">Todos</option>
          {CENTRO_DE_CUSTO.map((centro) => (
            <option key={centro} value={centro}>
              {ROTULO_CENTRO_DE_CUSTO[centro]}
            </option>
          ))}
        </select>
      </Campo>
      <Campo rotulo="Categoria">
        <select
          aria-label="Filtrar pela categoria"
          value={filtros.categoriaId ?? ''}
          onChange={(e) => mudar((p) => definirFiltro(p, 'categoria', e.target.value))}
        >
          <option value="">Todas</option>
          {filtros.categoriaId && !categoriaConhecida && (
            <option value={filtros.categoriaId}>
              {categoriasQuery.isPending ? 'Carregando…' : 'Categoria indisponível'}
            </option>
          )}
          {entradas.length > 0 && (
            <optgroup label="Entradas">
              {entradas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </optgroup>
          )}
          {saidas.length > 0 && (
            <optgroup label="Saídas">
              {saidas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </Campo>
      <Campo rotulo="Origem">
        <Escolhas
          aria-label="Filtrar pela origem"
          opcoes={OPCOES_DE_ORIGEM}
          valor={filtros.origem ?? 'todas'}
          onChange={(valor) =>
            mudar((p) => definirFiltro(p, 'origem', valor === 'todas' ? undefined : valor))
          }
        />
      </Campo>
      {temFiltroNoExtrato(filtros) && (
        <Botao variante="fantasma" pequeno onClick={() => mudar(limparFiltrosDoExtrato)}>
          Limpar filtros
        </Botao>
      )}
    </div>
  );
}
